import { Deal, Promotion } from '~/gql/graphql'
import { useInBucket } from '~/hooks/use-in-bucket'

const SHOWN_PROMOTION_TYPES = ['uswitch-award', 'voucher', 'bill-credit', 'physical']
const TYPES_BEFORE_MID = ['city-fibre-next-gen-promotion', 'next-gen-promotion', 'voucher', 'bill-credit']

const GENERIC_PROMOTION = {
  createdAt: '1900-01-01',
  endDate: '1900-01-01',
  onlineOnly: true,
  startDate: '1900-01-01',
  titleOverriden: false,
  value: 0,
  worth: 0
}

export const useDealPromotions = ({ services, provider, promotions }: Deal) => {
  const isBlackFriday = useInBucket('experiment-black-friday-2023', 'variant')
  const isMs3BannerTest = useInBucket('experiment-trustpilothighlightreview', 'variant')
  const isMidContractPriceRiseTest = useInBucket('experiment-mid-contract-price-rise-details-badge', 'variant')
  const showMidContractPriceRiseUsp =
    useInBucket('experiment-broadband-asa-contract-pricing-extended', ['extreme', 'lightweight']) &&
    !isMidContractPriceRiseTest &&
    provider.contractTerms.isFixedContract

  const showMs3NextGenPromo = isMs3BannerTest && ['ms3'].includes(services.broadband?.networkName ?? '')

  const isCityFibreTest = useInBucket('experiment-broadband-city-fibre-trial', 'variant')
  const showCityFibreUsp = isCityFibreTest && ['cityfibre-trial'].includes(services.broadband?.networkName ?? '')

  const promotionItems = promotions.filter((promotion) => {
    if (promotion.blackFriday && !isBlackFriday) return false // remove black friday promotions if not in bucket

    if (showMidContractPriceRiseUsp && promotion.title.includes('mid-contract')) return false // remove dupe mid-contract usps

    return SHOWN_PROMOTION_TYPES.includes(promotion.type) // remove promotions that should not be shown
  })

  const nextGenFibreUsp = () => {
    if (showMs3NextGenPromo) {
      return {
        type: 'next-gen-promotion',
        title: 'Next gen full fibre',
        id: 'next-gen-promo',
        provider: provider.id,
        ...GENERIC_PROMOTION
      }
    } else if (showCityFibreUsp) {
      return {
        type: 'city-fibre-next-gen-promotion',
        title: 'Next gen full fibre',
        id: 'next-gen-promo',
        provider: provider.id,
        ...GENERIC_PROMOTION
      }
    } else {
      return null
    }
  }

  const allPromotions =
    showMs3NextGenPromo || showCityFibreUsp ? [nextGenFibreUsp(), ...promotionItems] : promotionItems

  if (!showMidContractPriceRiseUsp) {
    return allPromotions
  }

  const midContractPriceRiseUsp = {
    type: 'no-mid-contact-rise',
    title: 'No mid-contract price rises',
    id: 'mid-contract-price-rise',
    provider: provider.id,
    ...GENERIC_PROMOTION
  }

  if (allPromotions.length === 0) {
    return [midContractPriceRiseUsp]
  }

  const insertionIdx = allPromotions.reduce((acc, cur, idx) => (TYPES_BEFORE_MID.includes(cur.type) ? idx : acc), -1)

  if (insertionIdx === -1) {
    return [midContractPriceRiseUsp, ...allPromotions]
  }

  return allPromotions.reduce<Promotion[]>(
    (acc, cur, idx) => (idx === insertionIdx ? [...acc, cur, midContractPriceRiseUsp] : [...acc, cur]),
    []
  )
}
